import React, { useState, useEffect } from "react";
import { getPurchases, exportPurchases } from "../../../services/purchase.service";
import { useNavigate, NavigateFunction } from "react-router-dom";
import EditIcon from '@mui/icons-material/Edit';
import {
  DataGrid,
  GridColDef,
  GridSortModel,
  GridPaginationModel,
  GridActionsCellItem,
  GridRowId,
  GridRenderCellParams, GridToolbarContainer
} from '@mui/x-data-grid';
import {
  Chip,
  Link,
  TextField,
  SelectChangeEvent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button as MaterialButton,
  CircularProgress
} from "@mui/material";
import IPage from "../../../types/page.type";
import {Box, Breadcrumbs, Grid, Typography, Button} from "@mui/joy";
import { Experimental_CssVarsProvider as CssVarsProvider } from "@mui/material/styles";
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import SearchIcon from "@mui/icons-material/Search";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DownloadIcon from "@mui/icons-material/Download";
import IPurchase from "../../../types/purchase.type";
import {PurchaseApprovalStatusColors} from "../../../types/enumeration/PurchaseApprovalStatusColors";
import {PurchaseApprovalStatusNames} from "../../../types/enumeration/PurchaseApprovalStatusNames";
import {PurchaseApprovalStatus} from "../../../types/enumeration/PurchaseApprovalStatus";
import {EllipsisTooltip} from "../../utils/EllipsisTooltip";
import moment, {Moment} from "moment/moment";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterMoment} from "@mui/x-date-pickers/AdapterMoment";
import IProvider from "../../../types/provider.type";
import {getActiveProviders} from "../../../services/provider.service";
import {Autocomplete} from "@mui/lab";

const PurchasesList: React.FC = () => {
  let navigate: NavigateFunction = useNavigate();
  const [purchasesPage, setPurchasesPage] = useState<IPage>({ content: [], totalElements: 0});
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [sortColumn, setSortColumn] = useState("id");
  const [sortDirection, setSortDirection] = useState<any>("asc");
  const [keywordFilter, setKeywordFilter] = useState( "");
  const [periodFilter, setPeriodFilter] = useState( "");
  const [statusFilter, setStatusFilter] = useState( "-1");
  const [providerFilter, setProviderFilter] = useState( "-1");
  const [providers, setProviders] = useState<Array<IProvider>>([]);

  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 10,
  });

  const columns: GridColDef<IPurchase>[] = [
    {
      field: 'id',
      headerName: 'ID',
      headerAlign: 'center',
      align: 'center',
      maxWidth: 60,
      flex: 1
    },
    {
      field: 'invoiceNumber',
      headerName: 'Nro. Comprobante',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1
    },
    {
      field: 'rol',
      headerName: 'Tipo',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return <span >{params.row.invoiceType?.name}</span>;
      }
    },
    {
      field: 'provider',
      headerName: 'Proveedor',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth: 160,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        const providerName = params.row.provider?.name ?? "";
        return <EllipsisTooltip text={providerName} />;
      }
    },
    {
      field: 'imputationPeriod',
      headerName: 'Periodo',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.row.imputationPeriod) {
          return <span>-</span>;
        }

        const [year, month] = params.row.imputationPeriod as [number, number];
        return <span>{`${String(month).padStart(2, '0')}-${year}`}</span>;
      }
    },
    {
      field: 'totalAmount',
      headerName: 'Monto',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return <span >${`${params.row.totalAmount.toFixed(2)}`}</span>;
      }
    },
    {
      field: 'approvalStatus',
      headerName: 'Estado',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return <Chip color={PurchaseApprovalStatusColors[PurchaseApprovalStatus[params.row.approvalStatus as keyof typeof PurchaseApprovalStatus]]}
                     label={PurchaseApprovalStatusNames[PurchaseApprovalStatus[params.row.approvalStatus as keyof typeof PurchaseApprovalStatus]]}
                     sx={{width: 160, fontWeight: 'bold'}}/>;
      }
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      cellClassName: 'actions',
      getActions: ({ id }) => {
        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
        ];
      },
    },
  ];

  const handleAddClick = () => {
    navigate('create');
  };

  const handleEditClick = (id: GridRowId) => () => {
    navigate('edit/'+id)
  };

  const handleChangeKeyword = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setKeywordFilter(event.target.value)
  };

  const handlePaginationModelChange = (newPaginationModel: GridPaginationModel) => {
    setPaginationModel(newPaginationModel);
    fetchPurchases(newPaginationModel.page);
  };

  const handleSortModelChange = React.useCallback((sortModel: GridSortModel) => {
    let field = sortModel[0] ? sortModel[0].field : "id";
    let order = sortModel[0] ? sortModel[0].sort : "asc";
    fetchPurchases(paginationModel.page, paginationModel.pageSize, field, order);
    setSortColumn(field);
    setSortDirection(order);
  }, []);

  const handleChangeImputationPeriod = (value: Moment | null) => {
    if (!value) {
      setPeriodFilter("");
      return;
    }
    setPeriodFilter(moment(value).format("YYYY-MM"));
  };

  const handleChangeStatus = (event: SelectChangeEvent) => {
    setStatusFilter(event.target.value)
  };

  const buildPurchaseParams = (
    page?: number,
    size?: number,
    sort: string = sortColumn,
    order: any = sortDirection
  ) => {
    type Param = {[key: string] : string}
    const params: Param = {}

    if (page !== undefined) {
      params.page = String(page);
    }

    if (size !== undefined) {
      params.size = String(size);
    }

    params.sort = sort;
    params.order = order;

    if (keywordFilter) {
      params.keyword = keywordFilter;
    }

    if (periodFilter) {
      params.periodFilter = periodFilter
    }

    if (statusFilter !== '-1') {
      params.status = statusFilter;
    }

    if (providerFilter !== '-1') {
      params.providerId = providerFilter;
    }

    return params;
  };

  const fetchPurchases = async (page: number = paginationModel.page, size: number = paginationModel.pageSize, sort: string = sortColumn, order: any = sortDirection) => {
    setLoading(true);

    const params = buildPurchaseParams(page, size, sort, order);

    getPurchases(params).then( (response) => {
      setPurchasesPage(response.data)
      setLoading(false);
    })
  };

  const handleExportPurchases = async () => {
    try {
      setExporting(true);

      const params = buildPurchaseParams(undefined, undefined, sortColumn, sortDirection);

      await exportPurchases(params);
    } finally {
      setExporting(false);
    }
  };

  function EditToolbar() {
    return (
      <GridToolbarContainer sx={{paddingBottom: 1}}>
        <Box sx={{ flexGrow: 1 }} />
        <MaterialButton
          variant="outlined"
          color="success"
          endIcon={
            exporting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <DownloadIcon />
            )
          }
          onClick={handleExportPurchases}
        >
          Exportar Excel
        </MaterialButton>
      </GridToolbarContainer>
    );
  }

  const fetchProviders = async () => {
    getActiveProviders().then( (response) => {
      setProviders(response.data)
    })
  };

  useEffect(() => {
    fetchPurchases();
    fetchProviders();
  }, []);

  return (
    <Box
      sx={{
        px: { xs: 2},
        pt: { xs: 'calc(12px + var(--Header-height))' },
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        height: '100dvh',
        overflow: "auto"
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'start',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', px: { xs: 1} }}>
            <Breadcrumbs
              size="sm"
              aria-label="breadcrumbs"
              separator={<ChevronRightRoundedIcon/>}
              sx={{ pl: 0 }}
            >
              <Typography level="h4" component="h1">
                <ShoppingCartIcon fontSize="small" sx={{ fontSize: 30, marginTop: -1}} />
              </Typography>
              <Typography level="h4" component="h1">
                Control Financiero
              </Typography>
              <Typography level="h4" component="h1">
                Compras
              </Typography>
              <Typography level="h4" component="h1">
                <Link
                  underline={"none"}
                  onClick={() => handleAddClick()}
                  sx={{ cursor: 'pointer' }}
                >
                  <AddCircleIcon fontSize="small" sx={{ fontSize: 30, marginTop: -1}} /> Crear Nuevo
                </Link>
              </Typography>
            </Breadcrumbs>
          </Box>
          <Grid
            container
            rowSpacing={2}
            columnSpacing={2}
            marginBlock={1}
            marginInline={0}
            sx={{ width: '100%' }}
          >
            <Grid xl={6} md={6} sm={6} xs={12}>
              <CssVarsProvider>
                <TextField
                  fullWidth
                  id="keyword"
                  label="Buscar por Nro. Comprobante"
                  name="keyword"
                  variant="filled"
                  type="text"
                  onChange={handleChangeKeyword}
                  onKeyDown={(ev) => {
                    if (ev.key === 'Enter') {
                      fetchPurchases();
                      ev.preventDefault();
                    }
                  }}
                  slotProps={{ inputLabel: {shrink: true}}}
                />
              </CssVarsProvider>
            </Grid>

            <Grid xl={3} md={3} sm={3} xs={6}>
              <CssVarsProvider>
                <LocalizationProvider dateAdapter={AdapterMoment} localeText={{ clearButtonLabel: "Limpiar" }}>
                  <DatePicker
                    views={["year", "month"]}
                    name="start"
                    label="Periodo"
                    format="MM-YYYY"
                    onChange={handleChangeImputationPeriod}
                    slotProps={{
                      actionBar: {
                        actions: ["clear", "accept"],
                      },
                      textField: {
                        variant: 'filled',
                        fullWidth: true,
                        InputLabelProps: {
                          shrink:  true
                        }
                      }
                    }} />
                </LocalizationProvider>
              </CssVarsProvider>
            </Grid>

            <Grid xl={3} md={3} sm={3} xs={6}>
              <CssVarsProvider>
                <FormControl variant="filled" fullWidth>
                  <InputLabel id="status-label" shrink>Estado</InputLabel>
                  <Select
                    labelId="status-label"
                    id="status"
                    name="status"
                    label="status"
                    value={statusFilter}
                    onChange={handleChangeStatus}
                  >
                    <MenuItem key="-1" value="-1">Todos</MenuItem>
                    {Object
                      .keys(PurchaseApprovalStatus)
                      .map((key) => (
                        <MenuItem key={key} value={PurchaseApprovalStatus[key as keyof typeof PurchaseApprovalStatus]}>
                          {PurchaseApprovalStatusNames[PurchaseApprovalStatus[key as keyof typeof PurchaseApprovalStatus]]}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </CssVarsProvider>
            </Grid>

            <Grid xl={6} md={6} sm={6} xs={12}>
              <CssVarsProvider>
                <Autocomplete
                  id="provider"
                  getOptionLabel={(provider) =>
                    `${provider?.name || ''} - CUIT: ${provider?.cuit || '...'}`
                  }
                  options={providers}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  onChange={(e, value) => {
                    if (!value) {
                      setProviderFilter("-1");
                      return;
                    }
                    setProviderFilter(value.id);
                  }}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="filled"
                      name="provider"
                      label="Proveedor"
                      slotProps={{ inputLabel: { shrink: true } }}

                    />
                  )}
                />
              </CssVarsProvider>
            </Grid>

            <Grid xl={6} md={6} sm={6} xs={12}>
              <Button
                variant="outlined"
                fullWidth
                style={{height: 56}}
                endDecorator={<SearchIcon />}
                onClick={() => fetchPurchases()}
              >
                Buscar
              </Button>
            </Grid>
          </Grid>

          <Box sx={{ width: '100%', height: 680, typography: 'body1' }} ml={1} mt={0} pr={2}>
            <CssVarsProvider>
              <DataGrid
                slots={{toolbar: EditToolbar}}
                rows={purchasesPage.content}
                columns={columns}
                columnVisibilityModel={{
                  id: false,
                }}
                paginationModel={paginationModel}
                rowCount={purchasesPage.totalElements}
                sortingMode="server"
                onSortModelChange={handleSortModelChange}
                paginationMode="server"
                onPaginationModelChange={handlePaginationModelChange}
                loading={loading}
                disableRowSelectionOnClick
              />
            </CssVarsProvider>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PurchasesList;